// A page that IS a paid feature (Whale Alerts, Wall Street, Calendar…), on a
// plan that doesn't open it: the route stays, the section navigation stays,
// and the page shows what the feature is, its blurred outline and the next
// step. Real data is not loaded here at all.
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { pageByPath } from '@/app/nav';
import { ContentBlock } from '@/components/ui/Cards';
import { Icon, IconCircle } from '@/components/ui/Icon';
import { useOrca } from '@/features/ai/OrcaProvider';
import { useAskContext } from '@/features/ai/useAskContext';
import { lockLine, PLAN_LABEL, useCaps } from '@/hooks/useCaps';
import { usePlanCatalog, usePlans } from '@/hooks/queries';
import type { Feature } from '@/lib/features';
import { Hero, PageFrame } from '@/layouts/PageFrame';
import { useT } from '@/i18n';
import { moneyParts } from '@/lib/format';
import { lockedCtx } from './ask';
import { FEATURE_INFO } from './catalog';
import { ctaFor, LockedBlock, PlanBadge, useUpgrade } from './Upgrade';

/** Route guard: the page when the plan opens the feature, its preview otherwise. */
export function Gated({ feature, children }: { feature: Feature; children: ReactNode }) {
  const { can } = useCaps();
  return can(feature) ? <>{children}</> : <LockedPage feature={feature} />;
}

export function LockedPage({ feature }: { feature: Feature }) {
  const { t } = useT('plans');
  const { pathname } = useLocation();
  const page = pageByPath(pathname);
  const caps = useCaps();
  const plans = usePlans();
  const upgrade = useUpgrade();
  const { askAbout } = useOrca();
  const need = caps.need(feature);
  const info = FEATURE_INFO[feature]!;
  const offer = plans.find((p) => p.id === need);
  const planInfo = usePlanCatalog().plans.find((p) => p.id === need);
  const cta = ctaFor(need, caps);
  const ctx = lockedCtx(feature, need, caps.planName, caps.trialAvailable);
  // the Orca bar here explains the feature, it never answers with its data
  useAskContext(ctx, 1);
  const trial = cta.kind === 'trial';
  const money = offer ? moneyParts(Number(offer.prices[0].price)) : null;

  const hero = (
    <Hero
      page={page}
      titleSide={<PlanBadge plan={need} className="lg" />}
      intro={info.lead}
      delta={lockLine(need)}
      big={trial ? caps.trialDays : money ? `${money.pre}${money.int}` : undefined}
      cents={trial ? ` ${t('page.days', { count: caps.trialDays })}` : money ? `${money.frac}${money.post}` : undefined}
      note={trial ? <>{t('page.plusFree')}<br />{t('page.once')}</> : <>{t('page.perMonth', { plan: PLAN_LABEL[need] })}<br />{planInfo?.tagline}</>}
      visual={<span className="stc-mark lk-mark" aria-hidden="true"><i /><i /><i /><b><Icon name={info.icon} /></b></span>}
      cards={[
        { icon: 'ai', label: <>{t('page.ask1')}<br />{t('page.ask2')}</>, value: <em className="lk-mv">{t('page.ask')}</em>, onClick: () => askAbout(ctx) },
        { icon: 'card', label: <>{t('page.yourPlan')}<br />{caps.planName}</>, value: <em className="lk-mv">{t('page.plans')}</em>, to: '/profile#plans' },
      ]}
      right={
        <div className="tiles lk-tiles">
          {info.points.map((p, i) => (
            <div key={p} className="tile static lk-pt">
              <div className="tile-top"><IconCircle name={i === 0 ? info.icon : 'check'} /></div>
              <span className="tile-l">{[t('page.what'), t('page.how'), t('page.better')][i]}</span>
              <div className="tile-b"><b>{p}</b></div>
            </div>
          ))}
          {info.demo.slice(0, 2).map(([ic, line, s]) => (
            <div key={line} className="tile static lk-demo-t" aria-hidden="true">
              <div className="tile-top"><IconCircle name={ic} /></div>
              <span className="tile-l">{s}</span>
              <div className="tile-b"><b>{line}</b></div>
            </div>
          ))}
          <button type="button" className="tile hl lk-go" onClick={() => upgrade(feature)}>
            <div className="tile-top"><IconCircle name="arrow" /><PlanBadge plan={need} /></div>
            <span className="tile-l">{trial ? t('page.trialStep') : t('page.nextStep')}</span>
            <div className="tile-b"><b>{cta.label}</b></div>
          </button>
        </div>
      }
    />
  );
  const lower = (
    <ContentBlock id="lk-preview" span={12} className="lk-page" title={info.title} sub={t('page.previewSub', { lock: lockLine(need) })}>
      <LockedBlock feature={feature} title={`${info.title} — ${t(need === 'premium' ? 'lockLow.premium' : 'lockLow.plus')}`}>
        {info.lead} {t('page.hidden', { plan: caps.planName })}
      </LockedBlock>
      <Link className="stc-link lk-more" to="/profile#billing">{t('page.compare')}</Link>
    </ContentBlock>
  );
  return <PageFrame page={page} hero={hero} lower={lower} />;
}
